<template>
  <div class="signature-container">
    <canvas
      ref="canvas"
      class="signature-canvas"
      @mousedown="startDrawing"
      @mousemove="draw"
      @mouseup="stopDrawing"
      @mouseleave="stopDrawing"
      @touchstart="handleTouchStart"
      @touchmove="handleTouchMove"
      @touchend="stopDrawing"
    ></canvas>
    <div class="buttons">
      <ux-button size="sm" @click="clearSignature">
        Clear <ux-icon right icon="fa-times" />
      </ux-button>
    </div>
  </div>
</template>

<script>
export default {
  name: "Signature",
  props: {
    modelValue: {
      type: String,
      default: "",
    },
    maxWidth: {
      type: Number,
      default: 500,
    },
    aspectRatio: {
      type: Number,
      default: 2, // width:height ratio (2:1 means width is twice the height)
    },
    lineWidth: {
      type: Number,
      default: 2,
    },
    lineColor: {
      type: String,
      default: "", // Will be determined based on dark mode
    },
    detectDarkMode: {
      type: Boolean,
      default: true,
    },
  },
  emits: ["update:modelValue"],
  data() {
    return {
      canvas: null,
      ctx: null,
      drawing: false,
      lastX: 0,
      lastY: 0,
      currentWidth: 0,
      currentHeight: 0,
      observer: null,
      isDarkMode: false,
      prefersDarkModeQuery: null,
    };
  },
  computed: {
    effectiveLineColor() {
      // If lineColor prop is provided, use it
      if (this.lineColor) {
        return this.lineColor;
      }

      // Otherwise, determine based on dark mode
      return this.isDarkMode ? "#FFFFFF" : "#000000";
    },
  },
  mounted() {
    this.setupResizeObserver();
    this.setupDarkModeDetection();
    this.initCanvas();

    // If there's a value already, load it
    if (this.modelValue) {
      this.loadSignature(this.modelValue);
    }

    window.addEventListener("resize", this.handleResize);
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.handleResize);
    if (this.observer) {
      this.observer.disconnect();
    }

    // Remove dark mode listener
    if (this.prefersDarkModeQuery) {
      this.prefersDarkModeQuery.removeEventListener(
        "change",
        this.handleDarkModeChange,
      );
    }
  },
  methods: {
    setupResizeObserver() {
      // Use ResizeObserver for more accurate size tracking
      this.observer = new ResizeObserver(() => {
        this.handleResize();
      });
      this.observer.observe(this.$el);
    },

    setupDarkModeDetection() {
      if (!this.detectDarkMode) return;

      // Method 1: Check for prefers-color-scheme media query
      if (window.matchMedia) {
        this.prefersDarkModeQuery = window.matchMedia(
          "(prefers-color-scheme: dark)",
        );
        this.isDarkMode = this.prefersDarkModeQuery.matches;

        // Listen for changes in dark mode preference
        this.prefersDarkModeQuery.addEventListener(
          "change",
          this.handleDarkModeChange,
        );
      }

      // Method 2: Check for dark mode class/attribute on HTML or body element
      this.checkDarkModeClasses();
    },

    handleDarkModeChange(e) {
      this.isDarkMode = e.matches;
      this.updateCanvasColors();
    },

    checkDarkModeClasses() {
      // Common dark mode indicators on HTML or body elements
      const htmlElement = document.documentElement;
      const bodyElement = document.body;

      // Check for common class names or attributes
      if (
        htmlElement.classList.contains("dark-mode") ||
        htmlElement.classList.contains("dark-theme") ||
        htmlElement.classList.contains("dark") ||
        bodyElement.classList.contains("dark-mode") ||
        bodyElement.classList.contains("dark-theme") ||
        bodyElement.classList.contains("dark") ||
        htmlElement.getAttribute("data-theme") === "dark" ||
        bodyElement.getAttribute("data-theme") === "dark"
      ) {
        this.isDarkMode = true;
      }
    },

    updateCanvasColors() {
      if (this.ctx) {
        this.ctx.strokeStyle = this.effectiveLineColor;
      }
    },

    calculateDimensions() {
      // Get container width
      const containerWidth = this.$el.clientWidth;

      // Calculate the width (constrained by maxWidth)
      this.currentWidth = Math.min(containerWidth, this.maxWidth);

      // Calculate height based on the aspect ratio
      this.currentHeight = this.currentWidth / this.aspectRatio;
    },

    initCanvas() {
      this.canvas = this.$refs.canvas;
      this.ctx = this.canvas.getContext("2d");

      this.calculateDimensions();

      this.canvas.width = this.currentWidth;
      this.canvas.height = this.currentHeight;

      // Scale line width based on canvas size
      const scaleFactor = this.currentWidth / 300; // Reference to original default width
      this.ctx.lineWidth = this.lineWidth * Math.max(scaleFactor, 1);

      this.ctx.lineCap = "round";
      this.ctx.lineJoin = "round";
      this.ctx.strokeStyle = this.effectiveLineColor;
    },

    handleResize() {
      // Save the current image data
      const imageData = this.canvas.toDataURL();

      // Reinit the canvas with new dimensions
      this.initCanvas();

      // Restore the image
      if (imageData !== this.getEmptyCanvasDataUrl()) {
        this.loadSignature(imageData);
      }
    },

    startDrawing(event) {
      this.drawing = true;
      const { x, y } = this.getCoordinates(event);
      this.lastX = x;
      this.lastY = y;
    },

    draw(event) {
      if (!this.drawing) return;

      const { x, y } = this.getCoordinates(event);

      this.ctx.beginPath();
      this.ctx.moveTo(this.lastX, this.lastY);
      this.ctx.lineTo(x, y);
      this.ctx.stroke();

      this.lastX = x;
      this.lastY = y;

      this.updateModel();
    },

    stopDrawing() {
      this.drawing = false;
      this.updateModel();
    },

    handleTouchStart(event) {
      event.preventDefault();
      if (event.touches.length === 1) {
        this.startDrawing({
          clientX: event.touches[0].clientX,
          clientY: event.touches[0].clientY,
        });
      }
    },

    handleTouchMove(event) {
      event.preventDefault();
      if (event.touches.length === 1) {
        this.draw({
          clientX: event.touches[0].clientX,
          clientY: event.touches[0].clientY,
        });
      }
    },

    getCoordinates(event) {
      const rect = this.canvas.getBoundingClientRect();
      return {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      };
    },

    clearSignature() {
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
      this.updateModel();
    },

    updateModel() {
      const dataUrl = this.canvas.toDataURL("image/png");
      this.$emit("update:modelValue", dataUrl);
    },

    loadSignature(dataUrl) {
      const img = new Image();
      img.onload = () => {
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
        this.ctx.drawImage(img, 0, 0, this.canvas.width, this.canvas.height);
      };
      img.src = dataUrl;
    },

    getEmptyCanvasDataUrl() {
      const emptyCanvas = document.createElement("canvas");
      emptyCanvas.width = this.currentWidth;
      emptyCanvas.height = this.currentHeight;
      return emptyCanvas.toDataURL();
    },
  },
};
</script>

<style scoped lang="scss">
.signature-container {
  position: relative;
  display: block;
  width: 100%;
  max-width: 500px;
}

.signature-canvas {
  width: 100%;
  height: auto;
  border: 1px solid color-mix(in lab, currentColor 18%, transparent);
  border-radius: 0.5em;
  background-color: var(--bg1, color-mix(in lab, currentColor 5%, transparent));
  color: inherit;
  cursor: crosshair;
  box-shadow: 0 0.1em 0.3em rgba(#000, 0.1);
}

.buttons {
  position: absolute;
  bottom: 1em;
  right: 0.5em;
}
</style>
